/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, ViewEncapsulation, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { WatermarkConfig, WatermarkService } from './watermark.service';

/**
 * The watermark drawn over a document preview.
 *
 * A transparent layer above the viewer — it never intercepts a click and is
 * invisible to the file itself, which is the point: the repository's binary is
 * untouched and the mark appears for whoever is looking, with their own name
 * and the current time in the `{{placeholders}}`.
 *
 * Tiling is done by rotating an oversized field of marks rather than each mark
 * on its own: the marks stay axis-aligned (readable) inside a field that is
 * itself turned, so the pattern reaches the corners without any measurement.
 *
 * The same component renders the live preview on the settings screen by being
 * handed a `draft` configuration instead of reading the saved one.
 */
@Component({
  selector: 'app-watermark-overlay',
  standalone: true,
  imports: [CommonModule],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div
      class="aca-wm"
      *ngIf="on()"
      aria-hidden="true"
      [style.color]="cfg().color"
      [style.opacity]="cfg().opacity"
      [style.font-size.px]="cfg().fontSize"
    >
      <div class="aca-wm__field" *ngIf="cfg().layout === 'tiled'" [style.transform]="'rotate(' + cfg().rotation + 'deg)'">
        <span class="aca-wm__mark" *ngFor="let cell of cells">
          <img class="aca-wm__logo" *ngIf="cfg().logo" [src]="cfg().logo" alt="" [style.height.em]="logoEm()" [style.max-width.em]="logoEm() * 8" />
          <span class="aca-wm__text" *ngIf="text()">{{ text() }}</span>
        </span>
      </div>

      <span class="aca-wm__mark aca-wm__mark--center" *ngIf="cfg().layout === 'center'" [style.transform]="centerTransform()">
        <img class="aca-wm__logo" *ngIf="cfg().logo" [src]="cfg().logo" alt="" [style.height.em]="logoEm()" [style.max-width.em]="logoEm() * 8" />
        <span class="aca-wm__text" *ngIf="text()">{{ text() }}</span>
      </span>
    </div>
  `,
  styleUrl: './watermark-overlay.component.scss'
})
export class WatermarkOverlayComponent {
  private readonly watermarks = inject(WatermarkService);

  /** The document being viewed — fills `{{docName}}`. */
  readonly docName = input('');

  /** A configuration to draw instead of the saved one (the settings preview). */
  readonly draft = input<WatermarkConfig | null>(null);

  /**
   * The moment `{{dateTime}}` reports. Live views leave it unset and stamp the
   * instant they render; the settings preview passes a fixed time so the sample
   * does not flicker on every change detection pass.
   */
  readonly at = input<Date | null>(null);

  /** The configuration in force — the draft when there is one, else the saved one. */
  readonly cfg = computed(() => WatermarkService.sanitize(this.draft() ?? this.watermarks.settings()));

  /** The text with its placeholders filled. */
  readonly text = computed(() => this.watermarks.resolve(this.cfg().text, this.docName(), this.at() ?? new Date()));

  /** Whether anything is drawn at all. */
  readonly on = computed(() => this.cfg().enabled && (!!this.text().trim() || !!this.cfg().logo));

  /** The logo's height in em — the base 1.8em scaled by the administrator's choice. */
  readonly logoEm = computed(() => (1.8 * this.cfg().logoScale) / 100);

  /**
   * Enough marks to cover a generous screen with the field enlarged for
   * rotation — cells that fall outside are simply clipped.
   */
  readonly cells = new Array(96);

  centerTransform(): string {
    return `translate(-50%, -50%) rotate(${this.cfg().rotation}deg)`;
  }
}
